import type { Exchange } from './coalesce';
import { EmptyResponseError } from './errors';
import type { HttpError, HttpErrorOptions } from './errors';
import { checkedResultOf } from './results';
import { failureForStatus } from './status-failure';
import type { ObservedResponse, RequestOptions, UntracedResult } from './types';

/* ---------------------------------------------------------------------------
 * One attempt, from the moment it has an exchange to the moment it has an
 * answer about that exchange.
 *
 * WHY THE BOUNDARY IS HERE AND NOT EARLIER. Putting the whole
 * preparation-to-validation path behind one new async entry point changes the
 * scheduler-visible interval before `joinFlight`: a joiner can still be
 * preparing when the opener settles, miss the existing flight and open a second
 * transport after the test release has already drained. MEASURED on the
 * existing `shared-failures.test.ts` cases ER66, CO68, CO59 and CO60, which
 * time out under that wider boundary. So `request.ts` keeps every await from
 * live option preparation through the actual `joinFlight` call, and this file
 * starts only once the caller has an exchange in hand.
 *
 * WHAT STAYS OUT. No attempt or wire counter, no call observation state, no
 * `Deadline`, no raw-failure setter, no mutable context bag and no injected
 * discard callback. This file returns values; the call assigns them. That is
 * what keeps retry, cleanup and delivery in one owner, and it is enforced by
 * the negative type tests in `request-attempt.test-d.ts` rather than by this
 * comment.
 * ------------------------------------------------------------------------- */

/**
 * Everything one attempt needs after boarding, and nothing the call owns.
 *
 * Every field is readonly and either an immutable fact about the request that
 * was prepared, or a directed callback that publishes an observation. There is
 * deliberately no way to reach back into the call's state from here.
 */
export interface FinishRequestAttemptInput {
  /**
   * The narrowed options on the decoded path, and `undefined` under `raw`.
   *
   * Both halves are load-bearing: it selects the response validator, and its
   * absence is what marks the result as the caller's own answer, stream and
   * all, so the call knows it has a raw failure to hand over.
   */
  readonly decodedOptions: RequestOptions | undefined;
  /** The options as they will be reported, after every `before` handler ran. */
  readonly finalOptions: RequestOptions;
  /** Builds the error options for a failure attributed to this call. */
  readonly makeErrorOptions: (
    options: RequestOptions | undefined,
  ) => HttpErrorOptions;
  readonly method: string;
  /**
   * Publishes the exchange this attempt observed.
   *
   * Called before validation, so a validation failure still reports what the
   * upstream said.
   */
  readonly onExchangeObserved: (snapshot: ExchangeSnapshot) => void;
  /** Publishes what actually went out, which a joiner learns only here. */
  readonly onTransportSent: (snapshot: TransportSentSnapshot) => void;
  /**
   * Where the answer came from. Reported rather than decided here: the call
   * reads `joined` back to know whether this attempt adds to the request count.
   */
  readonly origin: AnswerOrigin;
  readonly requestId: string | undefined;
  /** The settled exchange. Boarding happened in `request.ts`. */
  readonly shared: Exchange;
  readonly url: string;
}

/**
 * Where one attempt's answer came from.
 *
 * @remarks
 * What each value means:
 *
 * - `'transport'` for an attempt that sent the request itself, alone or as the
 *   one that opened a flight.
 * - `'flight'` for an attempt that rode another caller's request.
 * - `'cache'` for an answer `coalesce.cache` kept. Nothing went out for it, so
 *   the attempt reports no request, no sent headers and no request id of the
 *   call that kept it.
 */
export type AnswerOrigin = 'cache' | 'flight' | 'transport';

/** What the call learns about the wire once an exchange exists. */
export interface TransportSentSnapshot {
  readonly requestId: string | undefined;
  readonly sentHeaders: Headers;
}

/** What the call learns about the answer, before the body is validated. */
export interface ExchangeSnapshot {
  readonly joined: boolean;
  readonly requestId: string | undefined;
  readonly response: ObservedResponse;
  readonly sentHeaders: Headers;
}

/**
 * The two ways one attempt can finish once it has an exchange.
 *
 * A closed union on purpose. `status-failure` carries `rawFailure` in the same
 * value as the failure it belongs to, so the call cannot transfer one without
 * the other; splitting them is how a raw stream gets orphaned.
 *
 * Everything else -- preparation, transport, decode, coalescing, validation and
 * signal arbitration -- still fails by throwing into the one attempt-local
 * `catch` in `request.ts`. There is no second failure builder here.
 */
export type FinishedRequestAttempt =
  | {
      readonly body: unknown;
      readonly joined: boolean;
      readonly kind: 'success';
      readonly requestId: string | undefined;
      readonly response: ObservedResponse;
      readonly result: UntracedResult<unknown>;
      readonly sentHeaders: Headers;
    }
  | {
      readonly body: unknown;
      readonly failure: HttpError;
      readonly joined: boolean;
      readonly kind: 'status-failure';
      readonly rawFailure: UntracedResult<unknown> | undefined;
      readonly requestId: string | undefined;
      readonly response: ObservedResponse;
      readonly sentHeaders: Headers;
    };

/**
 * Turns a settled exchange into this caller's own answer about it.
 *
 * @param input - The immutable request facts plus the two publication callbacks.
 * @returns The attempt's finished value. A non-ok status is returned as
 * `status-failure` rather than thrown, so the call can copy every observation
 * field into its own locals before that failure enters the existing envelope.
 *
 * @remarks
 * Per-caller work only. A non-ok status is an answer the flight delivered, not
 * a flight that failed, so every caller that joined still makes its own
 * delivery choice on it. A failed flight shares its failure instead, and that
 * one is thrown from inside the shared region so every joiner gets the same
 * object.
 */
export async function finishRequestAttempt(
  input: FinishRequestAttemptInput,
): Promise<FinishedRequestAttempt> {
  const { decodedOptions, finalOptions, shared } = input;

  // A caller holding no wire of its own rode the flight's one. Reported, not
  // decided: the call owns the counter, and it reads this to know whether this
  // attempt adds to it.
  const joined = input.origin === 'flight';
  // A kept answer names no request: the one that fetched it belonged to
  // another call, and its id in this caller's failure would point at it.
  const requestId = input.origin === 'cache' ? undefined : input.requestId;

  // Where a JOINER learns what actually went out. Its own attempt never reached
  // the transport, so without this it would report a call that sent nothing
  // while holding a body that plainly came from somewhere. For the opener this
  // is the same object it already set at the transport. A hit sent nothing,
  // so it publishes nothing, and the call keeps reporting no wire.
  if (input.origin !== 'cache') {
    input.onTransportSent({ requestId, sentHeaders: shared.sent });
  }

  input.onExchangeObserved({
    joined,
    requestId,
    response: shared.observed,
    sentHeaders: shared.sent,
  });

  // A successful decoded call without a value rejects here unless the caller
  // opted in. Asked before the validator, so strict mode never runs it on
  // `undefined`, and after it, so a validator cannot produce the absence it was
  // never allowed.
  const requireBody = (body: unknown): void => {
    if (
      shared.observed.ok &&
      decodedOptions !== undefined &&
      decodedOptions.allowEmptyResponse !== true &&
      body === undefined
    ) {
      throw new EmptyResponseError(
        input.method,
        input.url,
        input.makeErrorOptions(finalOptions),
      );
    }
  };
  requireBody(shared.result.body);

  // Not asked at all on a non-ok answer. The schema describes what a SUCCESSFUL
  // answer looks like, so running it on an error body would reject every
  // failure for the wrong reason: the caller would see a validation failure
  // where there was an HTTP one, and the status that caused it would be gone.
  const result = await checkedResultOf(
    input.method,
    input.url,
    shared.observed.ok ? decodedOptions?.validate?.response : undefined,
    shared.result,
    () => input.makeErrorOptions(finalOptions),
  );
  requireBody(result.body);

  const common = {
    body: result.body,
    joined,
    requestId,
    response: shared.observed,
    sentHeaders: shared.sent,
  } as const;

  if (!shared.observed.ok) {
    return {
      ...common,
      // After the body, not before: the failure carries what the upstream said,
      // and on the decoded path that is only known once it has been read.
      failure: failureForStatus(
        input.method,
        input.url,
        result,
        result.body,
        () => input.makeErrorOptions(finalOptions),
      ),
      kind: 'status-failure',
      // Held, not acted on. Under `raw` this result IS the answer, unread
      // stream and all, and whether the caller still receives it or it has to
      // be cancelled is the delivery decision, which is made outside this
      // pipeline on purpose.
      rawFailure: decodedOptions === undefined ? result : undefined,
    };
  }

  return { ...common, kind: 'success', result };
}
